import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  Plus,
  Trash2,
  Gift,
  Tag,
  BarChart3,
  Percent,
  SlidersHorizontal,
  ChevronLeft,
  ChevronRight,
  Check,
  type LucideIcon,
} from 'lucide-react';
import { Modal } from '@/components/ui/modal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { MultiSelect } from '@/components/ui/multi-select';
import { DatePicker } from '@/components/ui/date-picker';
import { useLocations } from '@/hooks/use-locations';
import { useDepartments } from '@/hooks/use-departments';
import { useCategories } from '@/hooks/use-categories';
import { useLocationStore } from '@/stores/location-store';
import type { Promotion, PromotionInput, PromotionType } from '@/hooks/use-promotions';
import { usePromotion } from '@/hooks/use-promotions';
import { toast } from '@/stores/toast-store';
import { useProductDirectory } from './promotion-product-directory';
import { PromotionProductTable } from './promotion-product-picker';
import { isWarehouseType } from '@/lib/location-type';

const PROMOTION_TYPE_LABELS: Record<PromotionType, string> = {
  buy_x_get_y_free: 'Buy X Get Y Free',
  tiered_pricing: 'Tiered Pricing',
  discounted_price: 'Discounted Price',
  buy_x_get_discount: 'Buy X Get Discount',
};

// Visual metadata for the type picker cards — an icon and a plain-language
// example per type. The example is illustrative only; it drives no logic.
const PROMOTION_TYPE_META: Record<PromotionType, { icon: LucideIcon; example: string }> = {
  buy_x_get_y_free: { icon: Gift, example: 'Buy 2 chips, get 1 free' },
  discounted_price: { icon: Tag, example: 'Set a flat promo unit price' },
  tiered_pricing: { icon: BarChart3, example: '3 for $5, 6 for $9…' },
  buy_x_get_discount: { icon: Percent, example: 'Buy 3+, take 15% off' },
};

interface Props {
  open: boolean;
  onClose: () => void;
  /** Edit mode source — when set, form submits as PATCH. */
  initial?: Promotion | null;
  /** Duplicate source — when set, form submits as CREATE prefilled from this
   *  record (name suffixed with " (Copy)", dates cleared). Ignored if `initial` is set. */
  duplicateFrom?: Promotion | null;
  onSubmit: (data: PromotionInput) => Promise<void>;
  saving: boolean;
}

// Convert an ISO timestamp to the value an <input type="datetime-local">
// expects (yyyy-MM-ddTHH:mm in local time). Strips seconds + timezone.
function isoToLocalInput(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// Convert a datetime-local string back to an ISO timestamp (UTC).
function localInputToIso(local: string): string {
  if (!local) return '';
  const d = new Date(local);
  return Number.isNaN(d.getTime()) ? '' : d.toISOString();
}

// The schedule pickers edit wall-clock time in the operator's own browser zone —
// the same semantics as the native datetime-local input they replace. Pin the
// DatePicker to that zone so the clock it shows matches the stored
// `YYYY-MM-DDTHH:mm` string (which is parsed/formatted via new Date / the helpers
// above, both browser-local).
const LOCAL_TZ = Intl.DateTimeFormat().resolvedOptions().timeZone;

// Start of *today* (local midnight) formatted for the native datetime-local
// picker. Used as the `min` attribute and the validation floor: a promotion may
// start or end at any time today or on a future day — only earlier calendar
// days are rejected. (A start whose time already passed earlier today is fine;
// the promo simply begins immediately.)
function todayFloorLocalInput(): string {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return isoToLocalInput(d.toISOString());
}

// Render a numeric value in a controlled <input type="number">. Treats NaN
// and undefined as empty string so the user can actually clear the field —
// otherwise `Number('')` collapses to 0 and a stale 0 gets stuck.
const numberInputValue = (n: unknown): number | '' => (Number.isFinite(n) ? (n as number) : '');

// Default config shape for the chosen type — keeps the sub-form mountable
// before the user fills anything in.
function defaultConfigFor(type: PromotionType): any {
  switch (type) {
    case 'buy_x_get_y_free':
      return { buyQty: 1, getQty: 1 };
    case 'tiered_pricing':
      return { tiers: [{ qty: 2, totalPrice: 0 }] };
    case 'discounted_price':
      return { price: 0 };
    case 'buy_x_get_discount':
      return { applyMode: 'once', tiers: [{ qty: 2, discountType: 'percent', discountValue: 10 }] };
  }
}

const BUY_X_GET_DISCOUNT_MODE_LABELS: Record<'once' | 'every' | 'after', string> = {
  once: 'Only once (discount the first X units)',
  every: 'Every X units (repeat per complete group of X)',
  after: 'On every unit after X (first X full price)',
};

export function PromotionForm({ open, onClose, initial, duplicateFrom, onSubmit, saving }: Props) {
  // The whole dashboard is location-scoped. Scope the product pickers to the
  // active location so a promotion is built from products that actually exist
  // at the store being managed (departments/categories are tenant-wide, so they
  // are not location-filtered).
  const { selectedLocationId } = useLocationStore();
  const { data: locationsData } = useLocations();
  // Departments/categories are tenant-wide, but for a location-scoped promotion
  // we only offer the ones that actually have products at the active location.
  const { data: deptData } = useDepartments({ locationId: selectedLocationId || undefined });
  const { data: catData } = useCategories({ locationId: selectedLocationId || undefined });
  // The list endpoint returns shallow promotion rows (no scope arrays). When
  // we open in edit OR duplicate mode, fetch the full record so locations /
  // dept / cat / product selections rehydrate correctly.
  const sourceId = initial?.id ?? duplicateFrom?.id;
  const { data: fullData } = usePromotion(open ? sourceId : undefined);
  // Resolved source: prefer the fetched full record, fall back to the shallow
  // row until it arrives. Null when neither editing nor duplicating.
  const sourceShallow = initial ?? duplicateFrom ?? null;
  const hydrated: Promotion | null | undefined = sourceShallow ? (fullData?.data ?? sourceShallow) : null;
  const isDuplicate = !initial && !!duplicateFrom;
  // Running-only restriction: editing a promotion that's currently within its
  // window allows only the end timestamp to change (per product requirement).
  // Open-ended bounds count as in-window: no start = already begun, no end =
  // hasn't ended.
  const isRunningEdit = useMemo(() => {
    if (!initial) return false;
    if (!initial.isActive) return false;
    const now = Date.now();
    const started = !initial.startsAt || now >= new Date(initial.startsAt).getTime();
    const notEnded = !initial.endsAt || now <= new Date(initial.endsAt).getTime();
    return started && notEnded;
  }, [initial]);
  const locations = locationsData?.data ?? [];
  const departments = (deptData?.data as any[]) ?? [];
  const categories = (catData?.data as any[]) ?? [];

  // ── form state ───────────────────────────────────────────────────────────
  const [name, setName] = useState('');
  const [type, setType] = useState<PromotionType>('buy_x_get_y_free');
  const [config, setConfig] = useState<any>(defaultConfigFor('buy_x_get_y_free'));
  const [startsAt, setStartsAt] = useState('');
  const [endsAt, setEndsAt] = useState('');
  // Lower bound (start of today) for the date pickers, captured when the dialog
  // opens and refreshed on every open so a long-lived list view doesn't keep a
  // stale floor.
  const [minLocal, setMinLocal] = useState<string>(todayFloorLocalInput);
  const [excludeDiscountedProducts, setExcludeDiscountedProducts] = useState(false);
  const [locationIds, setLocationIds] = useState<string[]>([]);
  const [includedDepts, setIncludedDepts] = useState<string[]>([]);
  const [excludedDepts, setExcludedDepts] = useState<string[]>([]);
  const [includedCats, setIncludedCats] = useState<string[]>([]);
  const [excludedCats, setExcludedCats] = useState<string[]>([]);
  // Specific product targeting. `appliesTo` works for every promotion type;
  // `reward` is only meaningful for buy_x_get_y_free when the free item
  // differs from the trigger item.
  const [appliesToProductIds, setAppliesToProductIds] = useState<string[]>([]);
  const [rewardProductIds, setRewardProductIds] = useState<string[]>([]);
  // Whether the "Advanced targeting" section (dept/category include-exclude +
  // the already-discounted flag) is expanded. Starts open every time the dialog
  // opens: users kept asking where these filters were because a collapsed panel
  // read as "not available". Still collapsible once they've seen it.
  const [advancedOpen, setAdvancedOpen] = useState(true);

  // The dialog is a two-step wizard: step 1 configures the deal (basics, type,
  // offer, schedule, locations, advanced targeting), step 2 picks the products
  // it applies to. Every required field lives on step 1, so step 2 can never
  // block a save on its own.
  const [step, setStep] = useState<1 | 2>(1);
  // True while the stacked "Select Products" picker is open — the outer Modal
  // hands over Escape and the focus trap for as long as it is.
  const [pickerOpen, setPickerOpen] = useState(false);

  // Resolved name / UOM / barcode / price for every selected product, so the
  // step-2 tables can render full rows without preloading the catalogue.
  const selectedProductIds = useMemo(
    () => [...appliesToProductIds, ...rewardProductIds],
    [appliesToProductIds, rewardProductIds],
  );
  const {
    rows: productRows,
    remember: rememberProducts,
    seed: seedProducts,
  } = useProductDirectory(open, selectedProductIds, selectedLocationId || undefined);

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const clearFieldError = (key: string) => {
    setFieldErrors((e) => {
      if (!e[key]) return e;
      const { [key]: _, ...rest } = e;
      return rest;
    });
  };

  // When the dialog opens for create vs edit, hydrate from the source of
  // truth (`hydrated` = fetched full record when editing, falling back to
  // the shallow list row until the fetch resolves). Re-runs once `fullData`
  // arrives so scope arrays populate as soon as the API responds.
  // Note: we deliberately call setState inside the effect — this *is* the
  // hydrate-on-open pattern, not derived state, because the cashier can
  // edit the fields after the initial fill and we don't want subsequent
  // re-renders to clobber their edits.
  //
  // The signature below is what "the source changed" actually means: a
  // different promotion, the shallow row being upgraded to the full record, a
  // new active location, or locations finishing their load. Keying off it
  // stops a background refetch (react-query refetches on window focus once the
  // 30s staleTime lapses) from handing us an equal-but-new `hydrated` object
  // and wiping the edits — and the wizard step — mid-session.
  const hydrationKey = `${sourceId ?? 'new'}|${fullData?.data ? 'full' : 'shallow'}|${selectedLocationId ?? ''}|${locations.length}`;
  const hydratedKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!open) {
      hydratedKeyRef.current = null;
      return;
    }
    if (hydratedKeyRef.current === hydrationKey) return;
    hydratedKeyRef.current = hydrationKey;
    /* eslint-disable react-hooks/set-state-in-effect */
    setMinLocal(todayFloorLocalInput());
    setStep(1);
    if (hydrated) {
      setName(isDuplicate ? `${hydrated.name} (Copy)` : hydrated.name);
      setType(hydrated.type);
      setConfig(hydrated.config ?? defaultConfigFor(hydrated.type));
      // For duplicate, force the user to pick a fresh window — copying the
      // source dates would usually be wrong (e.g. duplicating an expired promo).
      setStartsAt(isDuplicate ? '' : isoToLocalInput(hydrated.startsAt));
      setEndsAt(isDuplicate ? '' : isoToLocalInput(hydrated.endsAt));
      setExcludeDiscountedProducts(hydrated.excludeDiscountedProducts);
      setLocationIds(hydrated.locationIds ?? []);
      const inDepts = (hydrated.departments ?? []).filter((d) => d.mode === 'include').map((d) => d.id);
      const exDepts = (hydrated.departments ?? []).filter((d) => d.mode === 'exclude').map((d) => d.id);
      const inCats = (hydrated.categories ?? []).filter((c) => c.mode === 'include').map((c) => c.id);
      const exCats = (hydrated.categories ?? []).filter((c) => c.mode === 'exclude').map((c) => c.id);
      setIncludedDepts(inDepts);
      setExcludedDepts(exDepts);
      setIncludedCats(inCats);
      setExcludedCats(exCats);
      setAdvancedOpen(true);
      setAppliesToProductIds(
        (hydrated.products ?? []).filter((p) => p.role === 'applies_to' || p.role === 'trigger').map((p) => p.id),
      );
      setRewardProductIds((hydrated.products ?? []).filter((p) => p.role === 'reward').map((p) => p.id));
      // Seed names from the promotion payload so the step-2 tables read by name
      // straight away, and still name a product the products service can't
      // return (deleted, or no longer stocked at the active location).
      seedProducts(hydrated.products ?? []);
    } else {
      setName('');
      setType('buy_x_get_y_free');
      setConfig(defaultConfigFor('buy_x_get_y_free'));
      setStartsAt('');
      setEndsAt('');
      setExcludeDiscountedProducts(false);
      // Auto-assign the promotion to the active global location (the dropdown in
      // the top-right). Promotions don't run at warehouses, so only default to a
      // store location; the user can still adjust. Empty when no/invalid location.
      const defaultLocationIds =
        selectedLocationId && locations.some((l: any) => l.id === selectedLocationId && !isWarehouseType(l.type))
          ? [selectedLocationId]
          : [];
      setLocationIds(defaultLocationIds);
      setIncludedDepts([]);
      setExcludedDepts([]);
      setIncludedCats([]);
      setExcludedCats([]);
      setAppliesToProductIds([]);
      setRewardProductIds([]);
      setAdvancedOpen(true);
    }
    setFieldErrors({});
    /* eslint-enable react-hooks/set-state-in-effect */
  }, [open, hydrationKey, hydrated, isDuplicate, selectedLocationId, locations, seedProducts]);

  // Switching type resets config to the new shape so stale fields don't leak.
  const handleTypeChange = (next: PromotionType) => {
    setType(next);
    setConfig(defaultConfigFor(next));
  };

  // Promotions are store-only — warehouse locations don't run POS
  // checkouts, so offering them in the picker would just produce
  // unreachable scope rows. Server rejects them too as defense.
  //
  // The form's products/departments/categories are filtered to the active
  // global location (top-right dropdown), so the promotion must target that
  // same location. Lock the picker to it: the active location is selectable,
  // every other store is greyed out. To build a promotion for another store,
  // switch the global location dropdown.
  const locationOptions = useMemo(
    () =>
      locations
        .filter((l: any) => !isWarehouseType(l.type))
        .map((l: any) => ({
          value: l.id,
          label: l.nickname || l.name,
          disabled: !!selectedLocationId && l.id !== selectedLocationId,
        })),
    [locations, selectedLocationId],
  );
  const deptOptions = useMemo(() => departments.map((d) => ({ value: d.id, label: d.name })), [departments]);
  const catOptions = useMemo(() => categories.map((c) => ({ value: c.id, label: c.name })), [categories]);

  const validate = (): Record<string, string> => {
    const errors: Record<string, string> = {};
    if (!name.trim()) errors.name = 'Name is required';

    // Both bounds are optional — an ongoing promotion can omit either or both.
    // Floor is the start of today: any time today or a future day is allowed, so
    // a start whose time already slipped past earlier today still saves (the
    // promo just begins immediately). Only earlier calendar days are rejected.
    const floor = new Date();
    floor.setHours(0, 0, 0, 0);
    const floorMs = floor.getTime();
    const startsIso = startsAt ? localInputToIso(startsAt) : '';
    const endsIso = endsAt ? localInputToIso(endsAt) : '';
    if (startsAt && !startsIso) errors.startsAt = 'Invalid start date/time';
    if (endsAt && !endsIso) errors.endsAt = 'Invalid end date/time';
    // Reject only dates before today. Skip the start check when editing a
    // running promo — that field is disabled and preserves its historical value.
    if (!errors.startsAt && !isRunningEdit && startsIso && new Date(startsIso).getTime() < floorMs)
      errors.startsAt = 'Start date cannot be before today';
    if (!errors.endsAt && endsIso && new Date(endsIso).getTime() < floorMs)
      errors.endsAt = 'End date cannot be before today';
    if (!errors.startsAt && !errors.endsAt && startsIso && endsIso && new Date(endsIso) <= new Date(startsIso))
      errors.endsAt = 'End must be after start';

    if (locationIds.length === 0) errors.locationIds = 'Pick at least one location';

    // Type-specific config validation
    if (type === 'buy_x_get_y_free') {
      if (!Number.isFinite(config?.buyQty) || config.buyQty < 1) errors.buyQty = 'Buy quantity must be at least 1';
      if (!Number.isFinite(config?.getQty) || config.getQty < 1) errors.getQty = 'Free quantity must be at least 1';
    } else if (type === 'discounted_price') {
      if (!Number.isFinite(config?.price) || config.price <= 0) errors.price = 'Promo price must be greater than 0';
    } else if (type === 'tiered_pricing') {
      const tiers: Array<{ qty: number; totalPrice: number }> = config?.tiers ?? [];
      if (tiers.some((t) => !Number.isFinite(t.qty) || t.qty < 1))
        errors.tiers = 'Every tier needs a buy quantity of at least 1';
      else if (tiers.some((t) => !Number.isFinite(t.totalPrice) || t.totalPrice <= 0))
        errors.tiers = 'Every tier needs a total price greater than 0';
    } else if (type === 'buy_x_get_discount') {
      const tiers: Array<{ qty: number; discountType: 'percent' | 'amount'; discountValue: number }> =
        config?.tiers ?? [];
      if (tiers.some((t) => !Number.isFinite(t.qty) || t.qty < 1))
        errors.tiers = 'Every tier needs a buy quantity of at least 1';
      else if (tiers.some((t) => !Number.isFinite(t.discountValue) || t.discountValue <= 0))
        errors.tiers = 'Every tier needs a discount greater than 0';
      else if (tiers.some((t) => t.discountType === 'percent' && t.discountValue > 100))
        errors.tiers = 'Percent discount cannot exceed 100';
    }

    return errors;
  };

  // Every required field lives on step 1, so both "Next" and the final save run
  // the same check — the save just bounces back to step 1 to show what failed.
  const goToProducts = () => {
    const errors = validate();
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;
    setStep(2);
  };

  const handleSave = async () => {
    const errors = validate();
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      setStep(1);
      return;
    }

    // Empty → null so the API stores an open-ended bound.
    const startsIso = startsAt ? localInputToIso(startsAt) : null;
    const endsIso = endsAt ? localInputToIso(endsAt) : null;

    // For discounted_price, keep only per-product overrides whose product is
    // still a selected "specific product" and has a valid price; drop the rest
    // so deselecting a product also clears its orphaned override.
    let outgoingConfig = config;
    if (type === 'discounted_price') {
      const selected = new Set(appliesToProductIds);
      const productPrices: Array<{ productId: string; price: number }> = ((config?.productPrices as any[]) ?? [])
        .filter((p) => selected.has(p.productId) && Number.isFinite(p.price) && p.price > 0)
        .map((p) => ({ productId: p.productId, price: p.price }));
      outgoingConfig = productPrices.length ? { price: config.price, productPrices } : { price: config.price };
    }

    const departmentsPayload = [
      ...includedDepts.map((id) => ({ id, mode: 'include' as const })),
      ...excludedDepts.map((id) => ({ id, mode: 'exclude' as const })),
    ];
    const categoriesPayload = [
      ...includedCats.map((id) => ({ id, mode: 'include' as const })),
      ...excludedCats.map((id) => ({ id, mode: 'exclude' as const })),
    ];

    // For buy_x_get_y_free we may have BOTH a buy-product and a different
    // free-product. The buy-product is `applies_to` (works as both scope and
    // trigger); the free-product is `reward`. For every other type only
    // `applies_to` is meaningful — `reward` is silently dropped.
    const productsPayload = [
      ...appliesToProductIds.map((id) => ({ id, role: 'applies_to' as const })),
      ...(type === 'buy_x_get_y_free' ? rewardProductIds.map((id) => ({ id, role: 'reward' as const })) : []),
    ];

    try {
      await onSubmit({
        name: name.trim(),
        type,
        config: outgoingConfig,
        startsAt: startsIso,
        endsAt: endsIso,
        excludeDiscountedProducts,
        locationIds,
        departments: departmentsPayload,
        categories: categoriesPayload,
        products: productsPayload,
      });
      onClose();
    } catch (e: any) {
      toast.error(e?.error?.message || 'Failed to save promotion');
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={initial ? 'Edit Promotion' : isDuplicate ? 'Duplicate Promotion' : 'New Promotion'}
      widthClass="max-w-[920px]"
      suspendKeyboard={pickerOpen}
      footer={
        <div className="flex items-center gap-3">
          {step === 2 ? (
            <Button variant="secondary" onClick={() => setStep(1)} disabled={saving}>
              <ChevronLeft className="h-4 w-4" /> Back
            </Button>
          ) : (
            <span className="hidden text-xs text-muted-foreground sm:block">
              Changes push to the registers immediately.
            </span>
          )}
          <div className="ml-auto flex items-center gap-3">
            <Button variant="secondary" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            {step === 1 ? (
              <Button onClick={goToProducts} disabled={saving}>
                Next: Products <ChevronRight className="h-4 w-4" />
              </Button>
            ) : (
              <Button onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : initial ? 'Save Promotion' : 'Create Promotion'}
              </Button>
            )}
          </div>
        </div>
      }
    >
      {isRunningEdit && (
        <div className="mb-5 rounded-xl border border-amber-300 bg-amber-50 px-3.5 py-3 text-sm text-amber-900">
          This promotion is currently <strong>running</strong>. You can change its <strong>name</strong>, the{' '}
          <strong>prices</strong>, the <strong>products</strong> it applies to, the <strong>locations</strong> it runs
          at, and the <strong>end date</strong> — these push to the registers immediately. The type, quantities, and
          start date are locked; to change those, stop it and create a new one (or duplicate it).
        </div>
      )}

      <StepBar
        step={step}
        onBack={() => setStep(1)}
        onForward={goToProducts}
        productCount={appliesToProductIds.length + (type === 'buy_x_get_y_free' ? rewardProductIds.length : 0)}
      />

      <div className={cn('space-y-7', step !== 1 && 'hidden')}>
        {/* ── 1 · basics ───────────────────────────────────────────── */}
        <FormSection num={1} title="The basics">
          <div>
            <Label htmlFor="promo-name">
              Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="promo-name"
              className="mt-1"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                clearFieldError('name');
              }}
              placeholder="e.g. Weekend BOGO on chips"
              error={!!fieldErrors.name}
              aria-invalid={!!fieldErrors.name}
            />
            {fieldErrors.name && <p className="mt-1 text-xs text-red-500">{fieldErrors.name}</p>}
          </div>
        </FormSection>

        {/* ── 2 · deal type (visual picker replaces the old dropdown) ─ */}
        <FormSection num={2} title="What kind of deal?">
          <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
            {(Object.keys(PROMOTION_TYPE_LABELS) as PromotionType[]).map((t) => {
              const meta = PROMOTION_TYPE_META[t];
              const Icon = meta.icon;
              const selected = type === t;
              // Same rule as the old <select disabled>: type can't change after
              // create (reshapes config) or while the promo is running.
              const typeLocked = !!initial || isRunningEdit;
              return (
                <button
                  key={t}
                  type="button"
                  onClick={() => {
                    if (!typeLocked) handleTypeChange(t);
                  }}
                  aria-pressed={selected}
                  disabled={typeLocked && !selected}
                  className={cn(
                    'flex items-start gap-3 rounded-xl border p-3 text-left transition',
                    selected ? 'border-primary bg-primary/5' : 'border-border bg-white hover:border-primary/40',
                    typeLocked && !selected && 'cursor-not-allowed opacity-50',
                    typeLocked && selected && 'cursor-default',
                  )}
                >
                  <span
                    className={cn(
                      'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg',
                      selected ? 'bg-primary text-white' : 'bg-primary/10 text-primary',
                    )}
                  >
                    <Icon className="h-4 w-4" />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold text-foreground">{PROMOTION_TYPE_LABELS[t]}</span>
                    <span className="mt-0.5 block text-xs text-muted-foreground">{meta.example}</span>
                  </span>
                </button>
              );
            })}
          </div>
          {(!!initial || isRunningEdit) && (
            <p className="text-xs text-muted-foreground">
              The promotion type can't be changed after it's created — duplicate it to start a different deal.
            </p>
          )}
        </FormSection>

        {/* ── 3 · the offer (per-type config) ──────────────────────── */}
        <FormSection num={3} title="The offer">
          <div className="rounded-xl border border-border bg-muted/30 p-4">
            <TypeConfigSubForm
              type={type}
              config={config}
              onChange={(next) => {
                setConfig(next);
                clearFieldError('buyQty');
                clearFieldError('getQty');
                clearFieldError('price');
                clearFieldError('tiers');
              }}
              fieldErrors={fieldErrors}
              // While running, prices stay editable but quantities/structure lock.
              priceOnly={isRunningEdit}
            />
          </div>
        </FormSection>

        {/* ── 4 · schedule ─────────────────────────────────────────── */}
        <FormSection num={4} title="When it runs">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="promo-starts">Starts at</Label>
              <DatePicker
                mode="datetime"
                portal
                timezone={LOCAL_TZ}
                format="MM/DD/YYYY HH:mm"
                clearable
                className="mt-1"
                placeholder="MM/DD/YYYY HH:mm"
                value={startsAt ? new Date(startsAt) : null}
                onChange={(v) => {
                  setStartsAt(v instanceof Date ? isoToLocalInput(v.toISOString()) : '');
                  clearFieldError('startsAt');
                  clearFieldError('endsAt');
                }}
                // Picker floor is the start of today (any time today or later is
                // allowed). Skipped when the field is disabled (running-edit) so
                // it doesn't fight a server-supplied historical value.
                minDate={!isRunningEdit && minLocal ? new Date(minLocal) : undefined}
                disabled={isRunningEdit}
                error={!!fieldErrors.startsAt}
                aria-label="Starts at"
              />
              {fieldErrors.startsAt ? (
                <p className="mt-1 text-xs text-red-500">{fieldErrors.startsAt}</p>
              ) : (
                <p className="mt-1 text-xs text-muted-foreground">Optional — leave blank to start immediately.</p>
              )}
            </div>
            <div>
              <Label htmlFor="promo-ends">Ends at</Label>
              <DatePicker
                mode="datetime"
                portal
                timezone={LOCAL_TZ}
                format="MM/DD/YYYY HH:mm"
                clearable
                className="mt-1"
                placeholder="MM/DD/YYYY HH:mm"
                value={endsAt ? new Date(endsAt) : null}
                onChange={(v) => {
                  setEndsAt(v instanceof Date ? isoToLocalInput(v.toISOString()) : '');
                  clearFieldError('endsAt');
                }}
                // Floor is the start of today; the validator below also rejects
                // earlier calendar days.
                minDate={minLocal ? new Date(minLocal) : undefined}
                error={!!fieldErrors.endsAt}
                aria-label="Ends at"
              />
              {fieldErrors.endsAt ? (
                <p className="mt-1 text-xs text-red-500">{fieldErrors.endsAt}</p>
              ) : (
                <p className="mt-1 text-xs text-muted-foreground">Optional — leave blank for an ongoing promotion.</p>
              )}
            </div>
          </div>
        </FormSection>

        {/* ── 5 · scope ────────────────────────────────────────────── */}
        <FormSection num={5} title="What it applies to">
          <div>
            <Label>
              Locations <span className="text-red-500">*</span>
            </Label>
            <div className="mt-1">
              <MultiSelect
                options={locationOptions}
                selected={locationIds}
                onChange={(next) => {
                  setLocationIds(next);
                  clearFieldError('locationIds');
                }}
                placeholder="Pick at least one location"
                triggerLabel="Locations"
              />
            </div>
            {fieldErrors.locationIds ? (
              <p className="mt-1 text-xs text-red-500">{fieldErrors.locationIds}</p>
            ) : (
              <p className="mt-1 text-xs text-muted-foreground">
                Required — pick the location(s) where this promotion should apply. There is no "all locations" mode; a
                promotion only fires at the stores explicitly listed here.
              </p>
            )}
          </div>

          {/* Specific products moved to step 2 — see the products step below. */}

          {/* Department / category include-exclude filters + the
              already-discounted flag. Rendered open so the controls are visible
              without a click; still collapsible for anyone who wants the room. */}
          <details
            open={advancedOpen}
            onToggle={(e) => setAdvancedOpen((e.currentTarget as HTMLDetailsElement).open)}
            className="rounded-xl border border-border"
          >
            <summary className="flex cursor-pointer list-none items-center gap-2 px-3.5 py-3 text-sm font-medium text-foreground [&::-webkit-details-marker]:hidden">
              <SlidersHorizontal className="h-4 w-4 text-muted-foreground" />
              Advanced targeting
              <span className="hidden text-xs font-normal text-muted-foreground sm:inline">
                departments, categories &amp; exclusions
              </span>
              {(() => {
                const n =
                  includedDepts.length +
                  excludedDepts.length +
                  includedCats.length +
                  excludedCats.length +
                  (excludeDiscountedProducts ? 1 : 0);
                return n > 0 ? (
                  <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-semibold text-primary">
                    {n}
                  </span>
                ) : null;
              })()}
              <ChevronRight
                className={cn(
                  'ml-auto h-4 w-4 text-muted-foreground transition-transform',
                  advancedOpen && 'rotate-90',
                )}
              />
            </summary>
            <div className="space-y-3 border-t border-border px-3.5 py-4">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <Label className="flex items-center gap-1.5">
                    <span className="rounded bg-success/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-success">
                      Include
                    </span>
                    Departments
                  </Label>
                  <div className="mt-1">
                    <MultiSelect
                      options={deptOptions}
                      selected={includedDepts}
                      onChange={setIncludedDepts}
                      placeholder="Any department"
                    />
                  </div>
                </div>
                <div>
                  <Label className="flex items-center gap-1.5">
                    <span className="rounded bg-destructive/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-destructive">
                      Exclude
                    </span>
                    Departments
                  </Label>
                  <div className="mt-1">
                    <MultiSelect
                      options={deptOptions}
                      selected={excludedDepts}
                      onChange={setExcludedDepts}
                      placeholder="None excluded"
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <Label className="flex items-center gap-1.5">
                    <span className="rounded bg-success/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-success">
                      Include
                    </span>
                    Categories
                  </Label>
                  <div className="mt-1">
                    <MultiSelect
                      options={catOptions}
                      selected={includedCats}
                      onChange={setIncludedCats}
                      placeholder="Any category"
                    />
                  </div>
                </div>
                <div>
                  <Label className="flex items-center gap-1.5">
                    <span className="rounded bg-destructive/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-destructive">
                      Exclude
                    </span>
                    Categories
                  </Label>
                  <div className="mt-1">
                    <MultiSelect
                      options={catOptions}
                      selected={excludedCats}
                      onChange={setExcludedCats}
                      placeholder="None excluded"
                    />
                  </div>
                </div>
              </div>

              <label className={`flex items-start gap-2 text-sm ${isRunningEdit ? 'opacity-60' : ''}`}>
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={excludeDiscountedProducts}
                  onChange={(e) => setExcludeDiscountedProducts(e.target.checked)}
                  disabled={isRunningEdit}
                />
                <span>Don't apply to products that already have an individual discount</span>
              </label>
            </div>
          </details>
        </FormSection>
      </div>

      {/* ── step 2 · the products the promotion targets ─────────────── */}
      <div className={cn('space-y-8', step !== 2 && 'hidden')}>
        <div>
          <h3 className="text-base font-semibold text-foreground">What products does it apply to?</h3>
          <p className="mt-0.5 text-sm text-muted-foreground">
            {type === 'buy_x_get_y_free'
              ? 'Choose the "buy" items that trigger the deal, and the free items customers get.'
              : 'Choose the products eligible for this promotion.'}
          </p>
        </div>

        <PromotionProductTable
          title="Applicable Products"
          selectedIds={appliesToProductIds}
          onChange={setAppliesToProductIds}
          rows={productRows}
          onRemember={rememberProducts}
          locationId={selectedLocationId || undefined}
          onPickerOpenChange={setPickerOpen}
          emptyMessage="No specific products — the promotion applies to everything in scope."
          hint={
            type === 'buy_x_get_y_free'
              ? 'These are the "buy" items. If no department, category or product is selected anywhere, the promotion applies to every product.'
              : type === 'discounted_price'
                ? 'Leave a Promo Price blank to charge the default promo unit price from step 1. If no department, category or product is selected anywhere, the promotion applies to every product.'
                : 'If no department, category or product is selected anywhere, the promotion applies to every product.'
          }
          // discounted_price supports a per-product override of the promo unit
          // price, so it rides along as a trailing column on the same row rather
          // than a second list the user has to reconcile by hand.
          extraColumn={
            type === 'discounted_price'
              ? {
                  header: 'Promo Price ($)',
                  width: 'w-36',
                  render: (row) => {
                    const entry = ((config?.productPrices as any[]) ?? []).find((p) => p.productId === row.id);
                    return (
                      <Input
                        className="w-28"
                        type="number"
                        min={0.01}
                        step={0.01}
                        placeholder="Default"
                        aria-label={`Promo price for ${row.name ?? row.id}`}
                        value={numberInputValue(entry?.price)}
                        onChange={(e) => {
                          const v = e.target.valueAsNumber;
                          const rest = ((config?.productPrices as any[]) ?? []).filter((p) => p.productId !== row.id);
                          setConfig({
                            ...config,
                            productPrices:
                              Number.isFinite(v) && v > 0 ? [...rest, { productId: row.id, price: v }] : rest,
                          });
                        }}
                      />
                    );
                  },
                }
              : undefined
          }
        />

        {type === 'buy_x_get_y_free' && (
          <PromotionProductTable
            title="Free Items"
            selectedIds={rewardProductIds}
            onChange={setRewardProductIds}
            rows={productRows}
            onRemember={rememberProducts}
            locationId={selectedLocationId || undefined}
            onPickerOpenChange={setPickerOpen}
            pickerTitle="Select Free Items"
            addLabel="Add Multiple Free Items"
            searchPlaceholder="Search for a free item to add"
            emptyMessage="No free items — customers get the same product they bought."
            hint="Leave empty if the free item is the same as the buy item (e.g. buy 2 get 1 of the same). Pick different products for cross-promos (buy a burger, get fries free)."
          />
        )}
      </div>
    </Modal>
  );
}

// The wizard's progress rail. Step 1 is always reachable; stepping forward runs
// the same validation the footer's Next button does, so a half-filled deal
// can't be skipped past.
function StepBar({
  step,
  onBack,
  onForward,
  productCount,
}: {
  step: 1 | 2;
  onBack: () => void;
  onForward: () => void;
  productCount: number;
}) {
  const steps = [
    { n: 1 as const, label: 'Promotion details', hint: 'Deal, schedule & targeting' },
    {
      n: 2 as const,
      label: 'Products',
      hint: productCount === 0 ? 'All products in scope' : `${productCount} selected`,
    },
  ];
  return (
    <div className="mb-6 flex items-center gap-2">
      {steps.map((s, i) => {
        const active = step === s.n;
        const done = step > s.n;
        return (
          <div key={s.n} className="flex min-w-0 flex-1 items-center gap-2">
            <button
              type="button"
              onClick={s.n === 1 ? onBack : onForward}
              aria-current={active ? 'step' : undefined}
              className={cn(
                'flex min-w-0 flex-1 items-center gap-2.5 rounded-xl border px-3 py-2 text-left transition',
                active ? 'border-primary bg-primary/5' : 'border-border bg-white hover:border-primary/40',
              )}
            >
              <span
                className={cn(
                  'flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold',
                  active || done ? 'bg-primary text-white' : 'bg-muted text-muted-foreground',
                )}
              >
                {done ? <Check className="h-3.5 w-3.5" strokeWidth={3} /> : s.n}
              </span>
              <span className="min-w-0">
                <span className="block truncate text-sm font-semibold text-foreground">{s.label}</span>
                <span className="block truncate text-xs text-muted-foreground">{s.hint}</span>
              </span>
            </button>
            {i === 0 && <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />}
          </div>
        );
      })}
    </div>
  );
}

// A numbered, titled group of fields. Gives the single-column form a visible
// spine (1 · basics, 2 · deal type, …) instead of a wall of equal-weight
// labels. Purely presentational — no state, no logic.
function FormSection({ num, title, children }: { num: number; title: string; children: ReactNode }) {
  return (
    <section>
      <div className="mb-3 flex items-center gap-2">
        <span className="flex h-5 w-5 items-center justify-center rounded-md bg-primary/10 text-[11px] font-bold leading-none text-primary">
          {num}
        </span>
        <h3 className="text-xs font-semibold uppercase tracking-[0.06em] text-muted-foreground">{title}</h3>
      </div>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

// ── per-type config sub-forms ──────────────────────────────────────────────

function TypeConfigSubForm({
  type,
  config,
  onChange,
  fieldErrors,
  disabled = false,
  priceOnly = false,
}: {
  type: PromotionType;
  config: any;
  onChange: (next: any) => void;
  fieldErrors: Record<string, string>;
  disabled?: boolean;
  // When true (running promo), price inputs stay editable but quantity/tier
  // structure inputs lock. `disabled` still hard-locks everything.
  priceOnly?: boolean;
}) {
  const lockStructure = disabled || priceOnly;
  if (type === 'buy_x_get_y_free') {
    return (
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>
            Buy quantity (X) <span className="text-red-500">*</span>
          </Label>
          <Input
            className="mt-1"
            type="number"
            min={1}
            value={numberInputValue(config?.buyQty)}
            onChange={(e) => onChange({ ...config, buyQty: e.target.valueAsNumber })}
            error={!!fieldErrors.buyQty}
            aria-invalid={!!fieldErrors.buyQty}
            disabled={lockStructure}
          />
          {fieldErrors.buyQty && <p className="mt-1 text-xs text-red-500">{fieldErrors.buyQty}</p>}
        </div>
        <div>
          <Label>
            Free quantity (Y) <span className="text-red-500">*</span>
          </Label>
          <Input
            className="mt-1"
            type="number"
            min={1}
            value={numberInputValue(config?.getQty)}
            onChange={(e) => onChange({ ...config, getQty: e.target.valueAsNumber })}
            error={!!fieldErrors.getQty}
            aria-invalid={!!fieldErrors.getQty}
            disabled={lockStructure}
          />
          {fieldErrors.getQty && <p className="mt-1 text-xs text-red-500">{fieldErrors.getQty}</p>}
        </div>
      </div>
    );
  }

  if (type === 'discounted_price') {
    return (
      <div>
        <Label>
          Promo unit price <span className="text-red-500">*</span>
        </Label>
        <Input
          className="mt-1"
          type="number"
          min={0.01}
          step={0.01}
          value={numberInputValue(config?.price)}
          onChange={(e) => onChange({ ...config, price: e.target.valueAsNumber })}
          error={!!fieldErrors.price}
          aria-invalid={!!fieldErrors.price}
          disabled={disabled}
        />
        {fieldErrors.price && <p className="mt-1 text-xs text-red-500">{fieldErrors.price}</p>}
      </div>
    );
  }

  if (type === 'tiered_pricing') {
    const tiers: Array<{ qty: number; totalPrice: number }> = config?.tiers ?? [];
    return (
      <div className="space-y-2">
        {tiers.map((tier, i) => (
          <div key={i} className="flex items-end gap-2">
            <div className="flex-1">
              <Label>Buy quantity</Label>
              <Input
                type="number"
                min={1}
                value={numberInputValue(tier.qty)}
                onChange={(e) => {
                  const next = [...tiers];
                  next[i] = { ...tier, qty: e.target.valueAsNumber };
                  onChange({ ...config, tiers: next });
                }}
                disabled={lockStructure}
              />
            </div>
            <div className="flex-1">
              <Label>Total discounted price for this qty</Label>
              <Input
                type="number"
                min={0.01}
                step={0.01}
                value={numberInputValue(tier.totalPrice)}
                onChange={(e) => {
                  const next = [...tiers];
                  next[i] = { ...tier, totalPrice: e.target.valueAsNumber };
                  onChange({ ...config, tiers: next });
                }}
                disabled={disabled}
              />
            </div>
            <Button
              type="button"
              variant="destructive-outline"
              size="icon"
              onClick={() => {
                if (tiers.length === 1) return;
                const next = tiers.filter((_, idx) => idx !== i);
                onChange({ ...config, tiers: next });
              }}
              disabled={tiers.length === 1 || lockStructure}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ))}
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={() =>
            onChange({
              ...config,
              tiers: [...tiers, { qty: tiers.length + 2, totalPrice: 0 }],
            })
          }
          disabled={lockStructure}
        >
          <Plus className="h-4 w-4" /> Add tier
        </Button>
        {fieldErrors.tiers && <p className="mt-1 text-xs text-red-500">{fieldErrors.tiers}</p>}
      </div>
    );
  }

  // buy_x_get_discount
  const tiers: Array<{
    qty: number;
    discountType: 'percent' | 'amount';
    discountValue: number;
  }> = config?.tiers ?? [];
  const applyMode: 'once' | 'every' | 'after' = config?.applyMode ?? 'once';
  return (
    <div className="space-y-2">
      <div>
        <Label>Apply discount</Label>
        <select
          className="mt-1 h-9 w-full rounded-lg border border-input bg-white px-2 text-sm disabled:cursor-not-allowed disabled:opacity-60"
          value={applyMode}
          onChange={(e) => onChange({ ...config, applyMode: e.target.value as 'once' | 'every' | 'after' })}
          disabled={lockStructure}
        >
          {(Object.keys(BUY_X_GET_DISCOUNT_MODE_LABELS) as Array<'once' | 'every' | 'after'>).map((m) => (
            <option key={m} value={m}>
              {BUY_X_GET_DISCOUNT_MODE_LABELS[m]}
            </option>
          ))}
        </select>
        <p className="mt-1 text-xs text-muted-foreground">
          X is the buy quantity of the matched tier below. “Only once” discounts the first X units; “Every X units”
          repeats the discount per complete group of X; “After X” discounts every unit beyond the first X.
        </p>
      </div>
      {tiers.map((tier, i) => (
        <div key={i} className="flex items-end gap-2">
          <div className="flex-1">
            <Label>Buy quantity</Label>
            <Input
              type="number"
              min={1}
              value={numberInputValue(tier.qty)}
              onChange={(e) => {
                const next = [...tiers];
                next[i] = { ...tier, qty: e.target.valueAsNumber };
                onChange({ ...config, tiers: next });
              }}
              disabled={lockStructure}
            />
          </div>
          <div className="w-32">
            <Label>Discount type</Label>
            <select
              className="h-9 w-full rounded-lg border border-input bg-white px-2 text-sm disabled:cursor-not-allowed disabled:opacity-60"
              value={tier.discountType}
              onChange={(e) => {
                const next = [...tiers];
                next[i] = {
                  ...tier,
                  discountType: e.target.value as 'percent' | 'amount',
                };
                onChange({ ...config, tiers: next });
              }}
              disabled={lockStructure}
            >
              <option value="percent">Percent</option>
              <option value="amount">Amount ($)</option>
            </select>
          </div>
          <div className="flex-1">
            <Label>{tier.discountType === 'percent' ? 'Percent off' : 'Amount off'}</Label>
            <Input
              type="number"
              min={0}
              step={tier.discountType === 'percent' ? 1 : 0.01}
              value={numberInputValue(tier.discountValue)}
              onChange={(e) => {
                const next = [...tiers];
                next[i] = { ...tier, discountValue: e.target.valueAsNumber };
                onChange({ ...config, tiers: next });
              }}
              disabled={disabled}
            />
          </div>
          <Button
            type="button"
            variant="destructive-outline"
            size="icon"
            onClick={() => {
              if (tiers.length === 1) return;
              const next = tiers.filter((_, idx) => idx !== i);
              onChange({ ...config, tiers: next });
            }}
            disabled={tiers.length === 1 || lockStructure}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ))}
      <Button
        type="button"
        variant="secondary"
        size="sm"
        onClick={() =>
          onChange({
            ...config,
            tiers: [
              ...tiers,
              {
                qty: tiers.length + 2,
                discountType: 'percent',
                discountValue: 10,
              },
            ],
          })
        }
        disabled={lockStructure}
      >
        <Plus className="h-4 w-4" /> Add tier
      </Button>
      {fieldErrors.tiers && <p className="mt-1 text-xs text-red-500">{fieldErrors.tiers}</p>}
    </div>
  );
}
